import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';

import classNames from 'classnames';
import { LazyImage } from "components/Image/Index";

import * as fromProjects from 'store/Projects';

import { a } from '@react-spring/web';
import { useIntersect } from 'hooks/useIntersect';
import { useAnimateInSpring } from 'hooks/useAnimateInSpring';
import { initialOffset, defaultSpringConfig } from 'components/Animations/SpringProperties/SpringProperties';

import { useDraggable, IDraggableBounds } from 'hooks/useDraggable';
import { useLatest } from 'utils/Index';

interface IProps {
  animationReady: boolean;
  bodyContent: object;
}

interface IDragLineDot {
  opacity: number;
  offset: string;
}

export const ImageRevealSlider: React.FC<IProps> = ({ animationReady, ...props }) => {
  const bodyContent: fromProjects.IContentImageRevealSliderSection = props.bodyContent as fromProjects.IContentImageRevealSliderSection;
  const [dragLineDots, setDragLineDots] = useState<IDragLineDot[]>([
    {
      opacity: 1,
      offset: '100px',
    },
  ]);
  const [initialised, setInitialised] = useState(false);
  const backgroundRef = useRef<HTMLDivElement>(null);

  const [range, setRange] = useState(0);
  const latestRange = useLatest(range);

  const [imageHeight, setImageHeight] = useState(100);

  const [inViewRef, inView] = useIntersect({
    startObserving: animationReady,
    rootMargin: "-150px 0 -150px 0",
  });

  useLayoutEffect(() => {
    const updateDraggableRange = () => {
      const backgroundProps = backgroundRef.current.getBoundingClientRect();
      const width = backgroundProps.width;
      const newImageHeight = width * bodyContent.leftImage.image.heightRatio;

      setRange(width);
      setImageHeight(newImageHeight);
    };

    updateDraggableRange();

    // Setup event listeners on initial mount to keep the dimensions of the draggable area updated
    window.addEventListener("resize", updateDraggableRange);

    return () => {
      window.removeEventListener("resize", updateDraggableRange);
    };
  }, []);

  const bounds: IDraggableBounds = {
    y: [0, 0],
    x: [0, latestRange.current],
  };

  const [bind, isBeingDragged, xPosition, , setPosition] = useDraggable(range, bounds);

  useEffect(() => {
    const dotDistance = 20;
    const newImageHeight = imageHeight - 10; // Add in a bit of a buffer at the edges of the image so we don;t have dots right on the edges.

    let numberOfDots = Math.floor(newImageHeight / dotDistance);

    if ((numberOfDots % 2) === 0) // If we have an even number of dots, add one in so we have one in the centre, and equal numbers either sides
      numberOfDots++;

    const newDragLineDots: IDragLineDot[] = [];
    for (let i = 0; i < numberOfDots; i++) {
      let offset: string = '';
      let offsetI: number = 0;
      let opacity: number = 1;

      const offsetSignRemainder = i % 2; // switch the sign of the offset for each number - And only increase offset every two indexes
      if (offsetSignRemainder === 0) {  // even index dot
        offset += '+';
        offsetI = (i / 2);
      } else if (offsetSignRemainder === Math.round(offsetSignRemainder)) { // odd index dot
        offset += '-';
        offsetI = (i / 2) + (1 / 2);
      } else {
        offsetI = 0;
      }

      const offsetAmount = (offsetI * dotDistance);

      const halfImageHeight: number = newImageHeight / 2;

      const distanceOfDotFromEdge: number = halfImageHeight - offsetAmount;

      const percentageDistance: number = (distanceOfDotFromEdge < 100 ? distanceOfDotFromEdge / 100 : 1);

      opacity = opacity * percentageDistance;

      offset += offsetAmount + 'px';

      newDragLineDots.push(
        {
          opacity,
          offset,
        },
      );
    }
    setDragLineDots(newDragLineDots);

  }, [imageHeight]);


  useEffect(() => {
    if (inView && !initialised) {
      setPosition({ x: 0.5 * range, from: { x: range }, immediate: false, config: defaultSpringConfig });
      setInitialised(true);
    }
  }, [inView, initialised]);

  const gridBackgroundStyles = {
    backgroundColor: bodyContent.backgroundColor,
  };

  const animateInSpring = useAnimateInSpring(inView);

  const wrapperStyles = {
    width: '100%',
    opacity: animateInSpring.active,
    transform: animateInSpring.active.to(x => `translateY(calc(${initialOffset(x)}))`),
  };

  const leftImageRevealStyles = {
    width: xPosition.to(x => `calc( ${(x / range) * range}px )`),
    willChange: isBeingDragged ? `width` : ``,
  };

  const rightImageRevealStyles = {
    left: xPosition.to(x => `calc( ${(x / range) * range}px )`),
    willChange: isBeingDragged ? `left` : ``,
  };

  const draggableHandleStyles = {
    transform: xPosition.to(x => `translateX(${(x / range) * range}px)`),
    willChange: isBeingDragged ? `transform` : ``,
  };

  const getDragLineDots = useCallback(() => (
    dragLineDots.map((dot, index) => (
      <div
        key={index}
        className='dot'
        style={{
          opacity: dot.opacity,
          transform: `translateY(calc(-50% + ${dot.offset}))`,
        }}
      />
    ))
  ), [dragLineDots]);

  return (
    <div ref={inViewRef} className='imageRevealSliderWrapper'>
      <a.div style={wrapperStyles} ref={backgroundRef}>
        <a.div className='revealImageWrapper left' style={leftImageRevealStyles}>
          <LazyImage
            width={range + 'px'}
            heightRatio={bodyContent.leftImage.image.heightRatio}
            src={'/assets/images/work/' + bodyContent.leftImage.image.url}
            alt={bodyContent.leftImage.image.alt}
            animateImageIn={true}
            animationType={'fadeIn'}
          />
          <div className='imageTitleWrapper'>
            <div className='background' />
            <div className='title'>
              {bodyContent.leftImage.title}
            </div>
          </div>
        </a.div>
        <a.div
          className='revealImageWrapper right'
          style={rightImageRevealStyles}
        >
          <LazyImage
            width={range + 'px'}
            heightRatio={bodyContent.rightImage.image.heightRatio}
            src={'/assets/images/work/' + bodyContent.rightImage.image.url}
            alt={bodyContent.rightImage.image.alt}
            animateImageIn={true}
            animationType={'fadeIn'}
          />
          <div className='imageTitleWrapper'>
            <div className='background' />
            <div className='title'>
              {bodyContent.rightImage.title}
            </div>
          </div>
        </a.div>
        <a.div
          className={classNames(
            'draggable-handle',
            { dragging: isBeingDragged },
          )}
          style={draggableHandleStyles}
          {...bind()}
        >
          <div className={'dragLine'}>
            {getDragLineDots()}
          </div>
          <div className={'dragHandIcon'} />
        </a.div>
      </a.div>
    </div>
  );
};
